import { getAccessToken } from "../authentication/supabaseService";

export interface PriceAlert {
    id: string;
    restaurantPlatformId: string;
    articleId: string;
    productName: string;
    fromPrice: number;
    fromDate: string;
    toPrice: number;
    toDate: string;
    fromOrderHistoryId: string;
    toOrderHistoryId: string;
    createdAt: string;
    platform?: string;
    category?: string;
    mainCategory?: string;
    sectionType?: 'FOOD' | 'DRINKS' | 'NON_FOOD';
    /**
     * The ordering account this alert's product came from is ONE account for both food and drinks,
     * so `sectionType` cannot decide the alert's side — `resolveSpendSide` uses `mainCategory`
     * instead. Derived server-side from restaurant_platforms.settings; read-only here.
     * Spec: z_ralph/tasks/1_new_tasks/spec-combined-ordering-accounts.md §5
     */
    combinedOrderingAccount: boolean;
  }
  
  export async function fetchPriceAlertsByPlatformIds(
    platformIds: string[]
  ): Promise<PriceAlert[]> {
    const token = await getAccessToken();
    const idsParam = encodeURIComponent(platformIds.join(','));
  
    const res = await fetch(`/api/priceAlerts/getByPlatforms?ids=${idsParam}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });    
  
    if (!res.ok) {
      throw new Error(`Failed to fetch price alerts: ${res.statusText}`);
    }
  
    const raw: any[] = await res.json();
    const data: PriceAlert[] = raw.map(mapBackendPriceAlert);
    return data;             
  }

  export async function fetchPriceAlertsForAllRestaurants(): Promise<PriceAlert[]> {
    const token = await getAccessToken();
  
    const res = await fetch(`/api/priceAlerts/getAllForAllRestaurants`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });    
  
    if (!res.ok) {
      throw new Error(`Failed to fetch price alerts for all restaurants: ${res.statusText}`);
    }
  
    const raw: any[] = await res.json();
    const data: PriceAlert[] = raw.map(mapBackendPriceAlert);
    return data;             
  }

  function mapBackendPriceAlert(a: any): PriceAlert {
    return {
      id: a.id,
      restaurantPlatformId: a.restaurant_platform_id,
      articleId: a.article_id,
      productName: a.product_name,
      fromPrice: parseFloat(a.from_price),
      fromDate: a.from_date,
      toPrice: parseFloat(a.to_price),
      toDate: a.to_date,
      fromOrderHistoryId: a.from_order_history_id,
      toOrderHistoryId: a.to_order_history_id,
      createdAt: a.created_at,
      platform: a.platform,
      category: a.category,
      mainCategory: a.main_category,
      sectionType: a.section_type,
      combinedOrderingAccount: a.combined_ordering_account === true
    };
  }
  